import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useSalesOrderDetailService from '../../../app/services/hooks/useSalesOrderDetailService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { SalesOrderDetailDataForm, defaultSalesOrderDetailDataForm } from '../../../app/models/SalesOrderDetail';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import useProductService from '../../../app/services/hooks/useProductService';
import useUnitService from '../../../app/services/hooks/useUnitService';
import useCurrencyService from '../../../app/services/hooks/useCurrencyService';
import { Product } from '../../../app/models/Product';
import { Unit } from '../../../app/models/Unit';
import { Currency } from '../../../app/models/Currency';
import SalesOrderDetailForm from './forms/SalesOrderDetailForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import LazyLoading from '../../../components/LazyLoading';
import useFetch from '../../../hooks/useFetch';
import { EndPoints } from '../../../app/services/EndPoints';

interface Props {
    salesOrderId: number;
    salesOrderDetailId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const SalesOrderDetailEdit = ({ salesOrderId, salesOrderDetailId, onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const { doGet } = useFetch();

    const { updateSalesOrderDetail, fetchingUpdateSalesOrderDetail } = useSalesOrderDetailService();
    const { getProducts } = useProductService();
    const { getUnits } = useUnitService();
    const { getCurrencies } = useCurrencyService();

    const [detail, setDetail] = useState<SalesOrderDetailDataForm>(defaultSalesOrderDetailDataForm);
    const [errorFields, setErrorFields] = useState<any>();
    const [products, setProducts] = useState<Product[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [loadingData, setLoadingData] = useState(true);
    const [fetchingEdit, setFetchingEdit] = useState(true);

    useEffect(() => {
        loadInitialData();
        edit();
    }, []);

    const loadInitialData = () => {
        // Cargar productos
        getProducts(
            { page: 1, per_page: 1000, sort: 'id', order: 'asc' },
            {
                onSuccess: (response: ServiceResponse) => {
                    setProducts(response.data.products || []);
                }
            }
        );

        // Cargar unidades
        getUnits(
            { page: 1, per_page: 1000, sort: 'id', order: 'asc' },
            {
                onSuccess: (response: ServiceResponse) => {
                    setUnits(response.data.units || []);
                }
            }
        );

        // Cargar monedas
        getCurrencies(
            { page: 1, per_page: 1000, sort: 'id', order: 'asc' },
            {
                onSuccess: (response: ServiceResponse) => {
                    setCurrencies(response.data.currencies || []);
                    setLoadingData(false);
                }
            }
        );
    };

    const edit = () => {
        doGet({
            url: EndPoints.SALES_ORDER_DETAILS.EDIT_SALES_ORDER_DETAIL.replace(':id', salesOrderDetailId.toString()),
            onSuccess: (response: ServiceResponse) => {
                const detailData = response.data.sales_order_detail;
                if (detailData) {
                    setDetail({
                        id: detailData.id,
                        sales_order_id: detailData.sales_order_id,
                        product_id: detailData.product_id,
                        quantity: detailData.quantity,
                        quantity_unit_id: detailData.quantity_unit_id,
                        price: detailData.price,
                        price_currency_id: detailData.price_currency_id,
                        price_unit_id: detailData.price_unit_id,
                        position: detailData.position,
                        order_date: detailData.order_date
                    });
                }
                setFetchingEdit(false);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                setFetchingEdit(false);
                if (onError) onError();
            }
        });
    };

    const update = () => {
        if (showLoading) showLoading('loading', 'Actualizando posición...');

        updateSalesOrderDetail(salesOrderDetailId, detail, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                toast.success(response.message, { autoClose: 2500 });
                setErrorFields(undefined);
                edit();
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (loadingData || fetchingEdit) {
        return <LazyLoading height={300} />;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <SalesOrderDetailForm
                        salesOrderDetail={detail}
                        setSalesOrderDetail={setDetail}
                        products={products}
                        units={units}
                        currencies={currencies}
                        errorFields={errorFields}
                    />
                </div>
            </div>
            
            <hr />
            
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm 
                        callbackCancel={cancel} 
                        locked={fetchingUpdateSalesOrderDetail} 
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm 
                        callbackSave={update} 
                        locked={fetchingUpdateSalesOrderDetail} 
                    />
                </div>
            </div>
        </>
    );
};

export default SalesOrderDetailEdit;

